import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, IconButton, SettingsIcon, Tooltip } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { columnsMapByType } from "~/components/ListView/common";
import { ListItem } from "~/components/ListView/ListItem";
import { useMyTasksColumnsContext } from "~/components/MyWork/MyTasks/context";
import { MyTasksViewSettingsDialog } from "~/components/MyWork/MyTasks/MyTasksViewSettingsDialog";
import { hasLoggedUserElevatedPermissions, isLoggedUserClientSelector } from "~/store/selectors/selector.users";

const UserWorkListHeader = ({ userId, hasScrollbar }) => {
  const { t } = useTranslation();
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const canEdit = useSelector(hasLoggedUserElevatedPermissions);
  const { columns = [] } = useMyTasksColumnsContext();
  const [isViewSettingsDialogOpen, setIsViewSettingsDialogOpen] = useState(false);

  const columnsMap = useMemo(() => {
    return columns
      .filter((col) => col.value && col.active)
      .map((col) => {
        return (
          <Body2
            weight="bold"
            className={`tw-hidden lg:tw-flex col col-${col.name.toLowerCase()} tw-pr-2 tw-justify-end`}
            key={`${col.name.toLowerCase()}-${col.name}`}
          >
            {columnsMapByType[col.name]}
          </Body2>
        );
      });
  }, [columns]);

  const openViewSettingsDialog = useCallback(() => setIsViewSettingsDialogOpen(true), []);

  const handleCloseViewSettings = useCallback(() => setIsViewSettingsDialogOpen(false), []);

  return (
    <>
      <ListItem className="tw-pl-7 tw-justify-between columns-header">
        <Body2
          weight="bold"
          className="col-name tw-flex tw-flex-1"
        >
          {t("Name")}
        </Body2>
        {columnsMap}
        {!isLoggedUserClient ? (
          <Tooltip title={t("Settings")}>
            <IconButton
              size="small"
              variant="text gray"
              className={classnames("tw-hidden lg:tw-flex tw-mr-1 tw-p-4", {
                "tw-invisible": userId,
                "tw-mr-[15px]": hasScrollbar,
              })}
              onClick={openViewSettingsDialog}
            >
              <SettingsIcon className="tw-shrink-0" />
            </IconButton>
          </Tooltip>
        ) : isLoggedUserClient && canEdit ? (
          <div
            className={classnames("tw-w-9", { "tw-mr-[15px]": hasScrollbar })}
            data-testid="header-settings-gap-column"
          />
        ) : null}
      </ListItem>
      {isViewSettingsDialogOpen && (
        <MyTasksViewSettingsDialog
          open={isViewSettingsDialogOpen}
          onClose={handleCloseViewSettings}
        />
      )}
    </>
  );
};

UserWorkListHeader.propTypes = {
  userId: PropTypes.number,
  hasScrollbar: PropTypes.bool,
};

export default UserWorkListHeader;
